import gql from 'graphql-tag'
import { GraphqlClient } from './GraphqlClient'
import { ApprovalStatus, Role, SubRole } from './Types'

export const login = async (email: string, password: string) => {
  const client = GraphqlClient()

  const LOGIN = gql`
    query ($email: String!, $password: String!) {
      login(email: $email, password: $password) {
        data {
          firstName
          lastName
          email
          role
          approverLevel
          id
          token
          phone
          programOffice {
            id
            title
          }
          approvalStatus
        }
        statusCode
        message
      }
    }
  `

  const results = await client.query({
    query: LOGIN,
    variables: { email, password }
  })

  return results
}

export const signup = async (
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  role: string,
  programOfficeId: number
) => {
  const client = GraphqlClient()

  const SIGNUP = gql`
    mutation (
      $firstName: String!
      $lastName: String!
      $email: String!
      $password: String!
      $role: String!
      $programOfficeId: Int
    ) {
      signup(
        firstName: $firstName
        lastName: $lastName
        email: $email
        password: $password
        role: $role
        programOfficeId: $programOfficeId
      ) {
        data {
          firstName
          lastName
          email
          role
          approverLevel
          id
          token
          phone
          programOffice {
            id
            title
          }
          approvalStatus
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: SIGNUP,
    variables: { firstName, lastName, email, password, role, programOfficeId }
  })

  return results
}

export const forgotPassword = async (email: string) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($email: String!) {
      forgotPassword(email: $email) {
        statusCode
        message
      }
    }
  `

  return await client.mutate({ mutation, variables: { email } })
}

export const verifyLoginCode = async (email: string, code: string) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($email: String!, $code: String!) {
      verifyLoginCode(email: $email, code: $code) {
        statusCode
        message
      }
    }
  `

  return await client.mutate({ mutation, variables: { email, code } })
}

export const resetPassword = async (email: string, password: string) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($email: String!, $password: String!) {
      resetPassword(email: $email, password: $password) {
        data {
          id
          firstName
          lastName
          email
          role
          approverLevel
          phone
          programOffice {
            id
            title
          }
          token
        }
        statusCode
        message
      }
    }
  `

  return await client.mutate({ mutation, variables: { email, password } })
}

export const updateApprovalStatus = async (
  userId: number,
  approvalStatus: ApprovalStatus
) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($userId: Int!, $approvalStatus: String!) {
      updateApprovalStatus(userId: $userId, approvalStatus: $approvalStatus) {
        statusCode
        message
      }
    }
  `

  return await client.mutate({
    mutation,
    variables: { userId, approvalStatus }
  })
}

export const updateRole = async (
  userId: number,
  role: Role,
  subRole: SubRole | null,
  approverLevel: number,
  programOfficeId: number,
  newEventApprover: number
) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation (
      $userId: Int!
      $role: String!
      $subRole: String
      $approverLevel: Int!
      $programOfficeId: Int
      $newEventApprover: Int
    ) {
      updateRole(
        userId: $userId
        role: $role
        subRole: $subRole
        approverLevel: $approverLevel
        programOfficeId: $programOfficeId
        newEventApprover: $newEventApprover
      ) {
        statusCode
        message
      }
    }
  `

  return await client.mutate({
    mutation,
    variables: { userId, role, subRole, approverLevel, programOfficeId, newEventApprover }
  })
}

export const updatePassword = async (userId: number, password: string) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($userId: Int!, $password: String!) {
      updatePassword(userId: $userId, password: $password) {
        statusCode
        message
      }
    }
  `

  return await client.mutate({ mutation, variables: { userId, password } })
}

export const findUserByEmail = async (email: string) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($email: String!) {
      findUserByEmail(email: $email) {
        data {
          id
          firstName
          lastName
          email
          role
          subRole
        }
        statusCode
        message
      }
    }
  `

  return await client.mutate({ mutation, variables: { email } })
}

export const deleteUser = async (userId: number, newEventOwner: number) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($userId: Int!, $newEventOwner: Int!) {
      deleteUser(userId: $userId, newEventOwner: $newEventOwner) {
        statusCode
        message
      }
    }
  `

  return await client.mutate({ mutation, variables: { userId, newEventOwner } })
}

export const getUsers = async (page: number, sortByRole: string) => {
  const client = GraphqlClient()

  const query = gql`
    query ($page: Int!, $sortByRole: String) {
      users(page: $page, sortByRole: $sortByRole) {
        id
        firstName
        lastName
        role
        subRole
        email
        approverLevel
        approvalStatus
        programOffice {
          id
          title
        }
        events
        approvalEvents
        createdAt
      }
    }
  `

  return await client.query({ query, variables: { page, sortByRole } })
}

export const getUsersCount = async () => {
  const client = GraphqlClient()

  const query = gql`
    query {
      usersCount
    }
  `

  return await client.query({ query })
}

export const userData = async () => {
  const client = GraphqlClient()

  const query = gql`
    query {
      userData {
        data {
          id
          role
          subRole
          approverLevel
          approvalStatus
          token
          phone
          programOffice {
            id
            title
          }
        }
        statusCode
        message
      }
    }
  `

  return await client.query({ query })
}

export const authenticated = () => {
  const user = localStorage.getItem('user')
  if (!user) {
    return null
  }
  return JSON.parse(user)
}

export const updateUser = async (
  userId: number,
  firstName?: string,
  lastName?: string,
  email?: string
) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation (
      $userId: Int!
      $firstName: String
      $lastName: String
      $email: String
    ) {
      updateUser(
        userId: $userId
        firstName: $firstName
        lastName: $lastName
        email: $email
      ) {
        statusCode
        message
      }
    }
  `

  return await client.mutate({
    mutation,
    variables: { userId, firstName, lastName, email }
  })
}
